// Country-package currency map — driven by backend IHRLocalizationProvider.DefaultCurrency values.
// Add rows here when a new country package is added to the backend.
const CURRENCY_META: Record<string, { symbol: string; locale: string }> = {
  PKR: { symbol: 'Rs',  locale: 'en-PK' },
  AED: { symbol: 'AED', locale: 'en-AE' },
  USD: { symbol: '$',   locale: 'en-US' },
  GBP: { symbol: '£',   locale: 'en-GB' },
  EUR: { symbol: '€',   locale: 'de-DE' },
  SAR: { symbol: 'SAR', locale: 'ar-SA' },
  OMR: { symbol: 'OMR', locale: 'ar-OM' },
  QAR: { symbol: 'QAR', locale: 'ar-QA' },
  KWD: { symbol: 'KWD', locale: 'ar-KW' },
  BHD: { symbol: 'BHD', locale: 'ar-BH' },
  INR: { symbol: '₹',   locale: 'en-IN' },
  BDT: { symbol: '৳',   locale: 'bn-BD' },
};

/**
 * Called once after login, before first render, when AppContext resolves the org's localization.
 * Writes the derived symbol + locale to localStorage so formatCurrency() picks them up app-wide
 * without a page reload.
 */
export const seedCurrencyFromCode = (currencyCode: string): void => {
  const entry = CURRENCY_META[currencyCode?.toUpperCase()] ?? CURRENCY_META['PKR'];
  localStorage.setItem('globalCurrencySymbol', entry.symbol);
  localStorage.setItem('globalCurrencyLocale', entry.locale);
};

export const getGlobalCurrencySymbol = (): string =>
  localStorage.getItem('globalCurrencySymbol') || 'Rs';

/** Alias for getGlobalCurrencySymbol — use this in new code */
export const getCurrencySymbol = getGlobalCurrencySymbol;

export const getGlobalCurrencyLocale = (): string =>
  localStorage.getItem('globalCurrencyLocale') || 'en-PK';

/** Manual user-driven currency switch (Settings page etc). Reloads to apply everywhere. */
export const setGlobalCurrency = (symbol: string, locale: string): void => {
  localStorage.setItem('globalCurrencySymbol', symbol);
  localStorage.setItem('globalCurrencyLocale', locale);
  window.location.reload();
};

export const formatCurrency = (amount: number | string | undefined | null): string => {
  const symbol = getGlobalCurrencySymbol();
  const locale = getGlobalCurrencyLocale();
  const val = typeof amount === 'string' ? parseFloat(amount) : amount;

  if (val === undefined || val === null || isNaN(val)) {
    return `${symbol} 0`;
  }

  return `${symbol} ${val.toLocaleString(locale, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

/**
 * Format a date using the org's locale (seeded from country package on login).
 * Accepts Date | string | null | undefined. Returns '—' for invalid/missing values.
 * Defaults to { dateStyle: 'medium' } — e.g. "Jul 2, 2026" in en-US, "02 Jul 2026" in en-PK.
 */
export const formatDate = (
  date: Date | string | null | undefined,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium' },
): string => {
  if (!date) return '—';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(getGlobalCurrencyLocale(), options);
};

/**
 * Format a date+time using the org's locale.
 * Defaults to { dateStyle: 'medium', timeStyle: 'short' }.
 */
export const formatDateTime = (
  date: Date | string | null | undefined,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' },
): string => {
  if (!date) return '—';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString(getGlobalCurrencyLocale(), options);
};

/**
 * Compact currency formatter for dashboard KPI cards.
 * e.g. Rs 1.2M, AED 500K, $ 2.3B
 */
export const formatCompactCurrency = (amount: number | undefined | null): string => {
  const symbol = getGlobalCurrencySymbol();
  const val = amount ?? 0;

  if (Math.abs(val) >= 1_000_000_000) return `${symbol} ${(val / 1_000_000_000).toFixed(1)}B`;
  if (Math.abs(val) >= 1_000_000)     return `${symbol} ${(val / 1_000_000).toFixed(1)}M`;
  if (Math.abs(val) >= 1_000)         return `${symbol} ${(val / 1_000).toFixed(1)}K`;
  return formatCurrency(val);
};
